import { CheckOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Empty, Select } from "antd";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { getUdfDisplayComponent } from "./udfFilteringFields";

const StyledAddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 6px 12px;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  font-size: 13px;
  color: #666;

  &:hover {
    border-color: #40a9ff;
    color: #40a9ff;
  }
`;

const StyledFilterBuilder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: #fafafa;
`;

const StyledFilterBuilderRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledSmallButton = styled.button`
  padding: 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #ff4d4f;
    color: #ff4d4f;

    /* Ensure icon inherits the hover color */
    .anticon {
      color: inherit;
    }
  }
`;

const StyledActiveFilter = styled.div`
  background: #f6f6f6;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const StyledActiveFilterText = styled.span`
  color: #333;
  font-weight: 500;
`;

const StyledRemoveButton = styled.button`
  padding: 2px;
  border: none;
  background: none;
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #ff4d4f;
  }
`;

const StyledTag = styled.span`
  background: #f0f0f0;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const StyledFlexContainer = styled.div`
  flex: 1;
`;

const StyledTagsContainer = styled.div`
  margin-bottom: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const StyledSmallCloseIcon = styled(CloseOutlined)`
  font-size: 10px;
  cursor: pointer;
`;

const StyledMediumCloseIcon = styled(CloseOutlined)`
  font-size: 12px;
`;

const StyledLargeCloseIcon = styled(CloseOutlined)`
  font-size: 14px;
`;

const StyledCheckIcon = styled(CheckOutlined)`
  font-size: 14px;
`;

const StyledSmallPlusIcon = styled(PlusOutlined)`
  font-size: 12px;
`;

const StyledSelect = styled(Select)`
  flex: 1;
`;

const StyledRootContainer = styled.div`
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
`;

const StyledEmptyWrapper = styled.div`
  margin: 8px 0;
`;

// Format value for storage based on field type
const formatUdfValueForType = (value, fieldType) => {
  if (value === null || value === undefined || value === "") return null;

  // Handle range objects for integer, decimal, and date fields
  if (fieldType === "date" || fieldType === "integer" || fieldType === "decimal") {
    // If it's a range object, return as-is
    return value;
  }
  if (fieldType === "boolean") {
    return typeof value === "boolean" ? value : value === "true";
  }
  return String(value);
};

// Custom hook for value management
const useUdfValueInput = (
  fieldType,
  selectedValues,
  setSelectedValues,
  setCurrentValue,
  currentValue,
) => {
  const addValue = useCallback(
    (value) => {
      const formatted = formatUdfValueForType(value, fieldType);
      // Allow false values for boolean type
      if (fieldType === "boolean" && formatted === false) {
        // false is a valid value for boolean type
      } else if (!formatted && formatted !== false) {
        return;
      }

      setSelectedValues((prev) => [...prev, formatted]);
      setCurrentValue(fieldType === "text" || fieldType === "string" ? "" : null);
    },
    [fieldType, setSelectedValues, setCurrentValue],
  );

  const removeValue = useCallback(
    (indexToRemove) => {
      setSelectedValues((prev) => prev.filter((_, index) => index !== indexToRemove));
    },
    [setSelectedValues],
  );

  // Check if current value can be added - memoized to avoid re-allocation
  // IMPORTANT: This component is used in two contexts with different data structures for date, integer, and decimal fields:
  // 1. Filter context: Uses range objects like { start: momentObj, end: momentObj } for dates or { min_value: 5, max_value: 10 } for numbers
  // 2. Form context: Uses simple values like moment objects/date strings for dates or plain numbers like 5 or 10.50
  // We must explicitly check if currentValue is a range object (has start/end or min_value/max_value properties) before validating it as a range.
  // Without this check, simple values would not be handled correctly.
  const canAddCurrentValue = useMemo(() => {
    if (fieldType === "date") {
      // Check if it's a range object with start and end
      if (
        currentValue &&
        typeof currentValue === "object" &&
        ("start" in currentValue || "end" in currentValue)
      ) {
        return (
          currentValue.start !== null &&
          currentValue.start !== undefined &&
          currentValue.end !== null &&
          currentValue.end !== undefined
        );
      }
      // Otherwise check if it's a valid single date value
      return currentValue !== null && currentValue !== undefined;
    }
    // Allow false values for boolean type
    if (fieldType === "boolean") {
      return currentValue === true || currentValue === false;
    }
    if (fieldType === "integer" || fieldType === "decimal") {
      // Check if it's a range object with min_value and/or max_value
      if (
        currentValue &&
        typeof currentValue === "object" &&
        ("min_value" in currentValue || "max_value" in currentValue)
      ) {
        // At least one value must be present and valid
        const hasValidMin = currentValue.min_value != null && !Number.isNaN(currentValue.min_value);
        const hasValidMax = currentValue.max_value != null && !Number.isNaN(currentValue.max_value);
        return hasValidMin || hasValidMax;
      }
      // Otherwise check if it's a valid number
      return currentValue != null && !Number.isNaN(parseFloat(currentValue));
    }
    // For number, check if it's a valid number
    if (fieldType === "number") {
      return currentValue != null && !Number.isNaN(parseFloat(currentValue));
    }
    return currentValue != null && currentValue !== "";
  }, [fieldType, currentValue]);

  return { addValue, removeValue, canAddCurrentValue };
};

function UdfCustomField({
  customFieldsData,
  value = {},
  onChange,
  placeholder = "Add custom field",
  isLoading = false,
  disabled = false,
  fieldComponents,
  allowMultipleValuesForAllFields = false, // When true, all fields support multiple values (like filters)
  valueAdapter,
}) {
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedField, setSelectedField] = useState("");
  const [currentValue, setCurrentValue] = useState(null); // Always start with null for controlled components
  const [selectedValues, setSelectedValues] = useState([]); // Temporary array for building multi-value fields

  // Convert external value to internal format
  const internalValue = useMemo(() => valueAdapter.toInternal(value), [value, valueAdapter]);

  // Wrap onChange to convert internal format back to external
  const handleInternalChange = useCallback(
    (newInternalValue) => {
      onChange(valueAdapter.fromInternal(newInternalValue));
    },
    [onChange, valueAdapter],
  );

  const fieldsMap = useMemo(() => {
    const dataArray = customFieldsData?.results || customFieldsData || [];
    if (!Array.isArray(dataArray)) return {};

    const fieldMap = {};
    dataArray
      .filter((field) => !field.is_archived) // Only active fields
      .forEach((fieldItem) => {
        fieldMap[fieldItem.slug] = {
          name: fieldItem.name,
          type: fieldItem.type,
          slug: fieldItem.slug,
          choices: fieldItem.choices || [], // Include choices for multi_select fields
        };
      });
    return fieldMap;
  }, [customFieldsData]);

  const availableFields = Object.values(fieldsMap);
  const hasFields = availableFields.length > 0;

  // Filter out already selected fields
  const fieldOptions = availableFields
    .filter((field) => !internalValue || !internalValue[field.slug])
    .map((field) => ({
      label: field.name,
      value: field.slug,
      type: field.type,
    }));

  // Get selected field type
  const getUdfSelectedFieldType = () => {
    if (!selectedField || !fieldsMap[selectedField]) return "text";
    return fieldsMap[selectedField].type;
  };

  // Custom hook for value management
  const udfFieldType = getUdfSelectedFieldType();
  const { addValue, removeValue, canAddCurrentValue } = useUdfValueInput(
    udfFieldType,
    selectedValues,
    setSelectedValues,
    setCurrentValue,
    currentValue,
  );

  // Check if field type allows multiple values
  const allowsMultipleValues = udfFieldType === "multi_select" || allowMultipleValuesForAllFields;

  // Render value input based on field type
  const renderUdfValueInput = () => {
    const Component = fieldComponents[udfFieldType] || fieldComponents.text;
    const selectedFieldData = fieldsMap[selectedField];

    return (
      <StyledFlexContainer>
        {/* Show accumulated values as tags when multiple values are allowed */}
        {allowsMultipleValues && selectedValues.length > 0 && (
          <StyledTagsContainer>
            {selectedValues.map((val, index) => {
              const UdfDisplayComponent = getUdfDisplayComponent(udfFieldType);
              return (
                <StyledTag key={`${selectedField}-${index}`}>
                  <UdfDisplayComponent value={val} />
                  <StyledSmallCloseIcon
                    onClick={() => removeValue(index)}
                    aria-label="Remove value"
                  />
                </StyledTag>
              );
            })}
          </StyledTagsContainer>
        )}

        <Component
          addValue={addValue}
          currentValue={currentValue}
          setCurrentValue={setCurrentValue}
          canAddCurrentValue={canAddCurrentValue}
          choices={selectedFieldData?.choices || []} // Pass choices for multi_select fields
        />
      </StyledFlexContainer>
    );
  };

  // Check if we can add field
  // For multi_select fields: need at least one value in selectedValues
  // For other fields: need a valid currentValue (no + button, so use currentValue directly)
  const canAddUdfFilter =
    !!selectedField &&
    (allowsMultipleValues
      ? selectedValues.length > 0 // multi_select needs at least one value in the list
      : canAddCurrentValue); // other fields just need a valid currentValue

  // Reset builder state
  const resetUdfBuilder = useCallback(() => {
    setSelectedField("");
    setCurrentValue(null);
    setSelectedValues([]);
    setShowBuilder(false);
  }, []);

  // Add field value
  const addUdfField = useCallback(() => {
    if (!canAddUdfFilter) return;

    let fieldValue;

    // For fields with multiple values, store as array
    if (allowsMultipleValues && selectedValues.length > 0) {
      // When allowMultipleValuesForAllFields is true, keep values in their original types
      fieldValue = allowMultipleValuesForAllFields
        ? selectedValues // Keep as-is for filters (mixed types)
        : selectedValues.map((v) => {
            // For form multi_select, convert to strings except booleans
            if (udfFieldType === "boolean") {
              return v; // Keep boolean values as-is
            }
            return String(v); // Convert to strings
          });
    } else {
      // For single value fields, format and use currentValue directly
      fieldValue = formatUdfValueForType(currentValue, udfFieldType);

      // Safety check - should not happen if canAddUdfFilter is working correctly
      if (fieldValue === null) {
        return;
      }
    }

    // Store as simple key-value object: { fieldSlug: value }
    handleInternalChange({
      ...internalValue,
      [selectedField]: fieldValue,
    });

    // Reset builder
    resetUdfBuilder();
  }, [
    canAddUdfFilter,
    selectedValues,
    selectedField,
    udfFieldType,
    handleInternalChange,
    internalValue,
    resetUdfBuilder,
    allowsMultipleValues,
    currentValue,
    allowMultipleValuesForAllFields,
  ]);

  // Remove field
  const removeUdfField = (fieldSlug) => {
    const newValue = { ...internalValue };
    delete newValue[fieldSlug];
    handleInternalChange(Object.keys(newValue).length > 0 ? newValue : null);
  };

  // Get display name for field
  const getUdfDisplayName = (fieldSlug) => {
    return fieldsMap[fieldSlug]?.name || fieldSlug;
  };

  // Custom empty component for Select
  const renderUdfEmpty = () => (
    <StyledEmptyWrapper>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
    </StyledEmptyWrapper>
  );

  // Get button placeholder text
  const getUdfButtonText = () => {
    if (!hasFields) return "No custom fields available";
    if (fieldOptions.length === 0) return "All fields set";
    return placeholder;
  };

  return (
    <StyledRootContainer $disabled={disabled}>
      {/* Active Custom Fields */}
      {internalValue &&
        Object.keys(internalValue).map((fieldSlug) => {
          const activeUdfFieldType = fieldsMap[fieldSlug]?.type;
          const UdfDisplayComponent = getUdfDisplayComponent(activeUdfFieldType);
          const udfFieldValue = internalValue[fieldSlug];

          return (
            <StyledActiveFilter key={fieldSlug}>
              <span>
                <StyledActiveFilterText>{getUdfDisplayName(fieldSlug)}</StyledActiveFilterText>:{" "}
                <UdfDisplayComponent value={udfFieldValue} />
              </span>
              <StyledRemoveButton
                onClick={() => !disabled && removeUdfField(fieldSlug)}
                aria-label={`Remove ${getUdfDisplayName(fieldSlug)}`}
                disabled={disabled}
              >
                <StyledMediumCloseIcon />
              </StyledRemoveButton>
            </StyledActiveFilter>
          );
        })}

      {/* Custom Field Builder */}
      {showBuilder ? (
        <StyledFilterBuilder
          onKeyDown={(e) => {
            // Prevent both default behavior and propagation for Enter key
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <StyledFilterBuilderRow>
            <StyledSelect
              popupMatchSelectWidth={false}
              placeholder={isLoading ? "Loading custom fields..." : "Select custom field..."}
              value={selectedField || undefined}
              onChange={(field) => {
                setSelectedField(field);
                setCurrentValue(null);
                setSelectedValues([]);
              }}
              options={fieldOptions}
              loading={isLoading}
              disabled={isLoading || !hasFields}
              notFoundContent={renderUdfEmpty()}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
            <StyledSmallButton onClick={resetUdfBuilder} aria-label="Cancel">
              <StyledLargeCloseIcon />
            </StyledSmallButton>
          </StyledFilterBuilderRow>

          {selectedField && (
            <StyledFilterBuilderRow
              onKeyDown={(e) => {
                // Centralized Enter key handling for all input types
                if (e.key === "Enter" && canAddCurrentValue) {
                  e.preventDefault();
                  e.stopPropagation();
                  addValue(currentValue);
                }
              }}
            >
              {renderUdfValueInput()}
              <StyledSmallButton
                onClick={addUdfField}
                disabled={!canAddUdfFilter}
                $disabled={!canAddUdfFilter}
                aria-label="Confirm and add"
              >
                <StyledCheckIcon />
              </StyledSmallButton>
            </StyledFilterBuilderRow>
          )}
        </StyledFilterBuilder>
      ) : (
        <StyledAddButton
          onClick={() => !disabled && setShowBuilder(true)}
          disabled={disabled || !hasFields || fieldOptions.length === 0}
          $disabled={disabled || !hasFields || fieldOptions.length === 0}
        >
          <StyledSmallPlusIcon />
          {getUdfButtonText()}
        </StyledAddButton>
      )}
    </StyledRootContainer>
  );
}

export default UdfCustomField;
